import React, {useRef, useState} from "react";
import _ from "lodash";

import {$store} from "@isudio/globals";
import {pages, policies} from "@isudio/config";
import {$core} from "@isudio/core";
import {cn} from "@isudio/utils";
import {type IApplyJobPayload} from "@isudio/data";
import {RowContainer, RowGrid, RowFlex, InputValidator, GradientButton} from "../../";

interface JobFormProps {
  jobId?: string;
  className?: string;
  minSize?: number;
  maxSize?: number;
  onSetLoading?: (loading: boolean) => void;
}

const JobForm = React.forwardRef<Element, JobFormProps>(
  ({
     jobId,
     className = "job",
     minSize = 0.005,
     maxSize = 20,
     onSetLoading
   }
  ) => {
    const nameRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);
    const resumeInputRef = useRef<HTMLInputElement | null>(null);
    const coverLetterInputRef = useRef<HTMLInputElement | null>(null);
    const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
    const [errorName, setErrorName] = useState<string | undefined>(undefined);
    const [errorEmail, setErrorEmail] = useState<string | undefined>(undefined);

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>, type: "resume" | "coverLetter") => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files?.[0];
        if (file) {
          if (minSize && file.size < minSize * 1024 * 1024) {
            _.toastError(`Please select a file which is at least ${minSize * 1024}KB in size.`);
            e.target.value = "";
            return;
          }

          if (maxSize && file.size > maxSize * 1024 * 1024) {
            _.toastError(`Please select a file which is less than ${maxSize}MB in size.`);
            e.target.value = "";
            return;
          }

          if (type === "resume") {
            setResumeFile(file);
          } else {
            setCoverLetterFile(file);
          }
        }
      }
    };

    const isValid =
      name.trim().length > 0 &&
      email.trim().length > 0 &&
      resumeFile !== null &&
      coverLetterFile !== null &&
      errorName === undefined &&
      errorEmail === undefined;

    const onSubmit = async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!nameRef.current || !emailRef.current) return;

      const formName = nameRef.current.value;
      if (!formName) {
        setErrorName("Name is required");
        return;
      }

      const formEmail = emailRef.current.value;
      if (!formEmail) {
        setErrorEmail("Email is required");
        return;
      }

      if (!resumeFile || !coverLetterFile) {
        _.toastError("Both Resume and Cover Letter are required files.");
        return;
      }

      // Assemble full upload payload containing string details and file objects
      const payload: IApplyJobPayload = _.omitBy({
        to: pages.careers.toEmail,
        jobId: jobId,
        name: formName,
        email: formEmail,
        resume: resumeFile,
        coverLetter: coverLetterFile,
        device: {
          ...$store.Device.data || null,
          ...{deviceTime: new Date().toISOString()}
        }
      }, _.isNil) as IApplyJobPayload;

      onSetLoading?.(true);
      await $core.applyJob(payload).then(() => {
        onSetLoading?.(false);
        _.toastSuccess(pages.careers.jobSubmitSuccessMessage);
        nameRef.current!.value = "";
        emailRef.current!.value = "";

      }).catch((e: any) => {
        onSetLoading?.(false);
        _.toastError(_.get(e, 'message'));
      });

      const t = setTimeout(async () => {
        onSetLoading?.(false);
        if (nameRef.current) nameRef?.current?.focus();
        setResumeFile(null);
        setCoverLetterFile(null);
        if (resumeInputRef.current) resumeInputRef.current.value = "";
        if (coverLetterInputRef.current) coverLetterInputRef.current.value = "";
        clearTimeout(t);
      }, 10);
    }

    const validateName = (value: string): string | undefined => {
      const name = value.trim();
      if (!name) return "Name is required.";
      if (name.length < userPolicy.name.minLength) return `Name should have at least ${userPolicy.name.minLength} characters.`;
      if (name.length > userPolicy.name.maxLength) return `Name can't exceed ${userPolicy.name.maxLength} characters.`;
      return undefined;
    };

    const validateEmail = (value: string): string | undefined => {
      const email = value.trim();
      if (!email) return "Email is required.";
      if (email.length < userPolicy.email.minLength) return `Email should have at least ${userPolicy.email.minLength} characters.`;
      if (email.length > userPolicy.email.maxLength) return `Email can't exceed ${userPolicy.email.maxLength} characters.`;
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) return "Please enter a valid email address.";
      return undefined;
    };

    return (
      <RowContainer
        className={cn(
          `relative grid`,
          className,
          jobId
        )}
      >
        <form
          onSubmit={onSubmit}
          className={`form-${className} grid mt-4 p-0 gap-4 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed`}>
          <RowGrid>
            <InputValidator
              ref={nameRef}
              placeholder="Name*"
              minLength={userPolicy.name.minLength}
              maxLength={userPolicy.name.maxLength}
              mask={/[a-zA-Z ]/}
              format="properCase"
              error={errorName}
              showToast={_.toastError}
              onChange={(e) => {
                const value = e.target.value;
                setName(value);
                setErrorName(validateName(value));
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await onSubmit();
                }
              }}
            />

            <InputValidator
              ref={emailRef}
              placeholder="Email*"
              minLength={userPolicy.email.minLength}
              maxLength={userPolicy.email.maxLength}
              mask={/[a-zA-Z0-9@._%+-]/}
              format="email"
              error={errorEmail}
              showToast={_.toastError}
              onChange={(e) => {
                const value = e.target.value;
                setEmail(value);
                setErrorEmail(validateEmail(value));
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await onSubmit();
                }
              }}
            />
          </RowGrid>

          <RowGrid>
            <div>
              <input
                ref={resumeInputRef}
                type="file"
                accept=".doc, .docx, .pdf"
                onChange={(e) => onSelectFile(e, "resume")}
                className="hidden"
              />

              <button
                className="w-full flex items-center justify-center gap-2 p-2 text-xs tracking-tight leading-none cursor-pointer focus:outline-none rounded-[6px] transition duration-200 text-ghost-white bg-custom-gray-550 hover:text-generic-hover hover:scale-105 hover:bg-custom-gray-500"
                type="button"
                tabIndex={-1}
                onClick={() => resumeInputRef.current?.click()}
              >
                <i className={`fa-duotone fa-light fa-file-user`} />
                <span className="truncate max-w-48">
                  {resumeFile ? `Resume: ${resumeFile.name}` : "Upload Resume*"}
                </span>
              </button>
            </div>

            <div>
              <input
                ref={coverLetterInputRef}
                type="file"
                accept=".doc, .docx, .pdf"
                onChange={(e) => onSelectFile(e, "coverLetter")}
                className="hidden"
              />
              <button
                className="w-full flex items-center justify-center gap-2 p-2 text-xs tracking-tight leading-none cursor-pointer focus:outline-none rounded-[6px] transition duration-200 text-ghost-white bg-custom-gray-550 hover:text-generic-hover hover:scale-105 hover:bg-custom-gray-500"
                type="button"
                tabIndex={-1}
                onClick={() => coverLetterInputRef.current?.click()}
              >
                <i className="fa-duotone fa-light fa-file" />
                <span className="truncate max-w-48">
                  {coverLetterFile ? `Cover Letter: ${coverLetterFile.name}` : "Upload Cover Letter*"}
                </span>
              </button>
            </div>
          </RowGrid>

          <RowFlex className="items-center justify-center">
            <GradientButton
              tabIndex={-1}
              type="submit"
              text="Submit Application"
              className={
                cn(
                  `w-48! btn-submit btn-${isValid ? "blue transition duration-200 hover:text-generic-hover hover:scale-105" : "gray"}`
                )}
              disabled={!isValid}
              onClick={onSubmit}
            />
          </RowFlex>
        </form>
      </RowContainer>
    )
  }
);

JobForm.displayName = "JobForm";
export {JobForm};